import type { ComponentProps } from "react";
import { Link } from "@tanstack/react-router";
import { tv } from "tailwind-variants";

import { ProgressChartPlot } from "@/domains/progress/components/progress-chart-plot";
import { PROGRESS_STATUS_CODE } from "@/domains/progress/models/constants";
import type { ProgressChart } from "@/domains/progress/models/types";
import { stashCareTeamMessageTopic } from "@/domains/progress/utils/care-team-message";
import { buildProgressChartCaption } from "@/domains/progress/utils/chart-copy";
import { Button, Card, Heading } from "@/ui";

const MESSAGE_CARE_TEAM_LABEL = "Message Your Care Team";

const cardLayout = tv({
  base: "flex flex-col gap-4 p-5 sm:p-6",
});

const statusPill = tv({
  base: "inline-flex shrink-0 items-center rounded-full border px-3 py-1 text-sm font-semibold",
  variants: {
    tone: {
      attention: "border-danger-line bg-danger-surface text-danger",
      neutral: "border-line bg-surface-sunken text-content-secondary",
      positive: "border-success-line bg-success-surface text-success",
      unknown: "border-line bg-surface-sunken text-content-secondary",
    },
  },
});

export type ProgressChartCardProps = ComponentProps<"article"> & {
  chart: ProgressChart;
  firstPointNote?: string;
  idealLineLead?: string;
};

export const ProgressChartCard = ({
  chart,
  className,
  firstPointNote,
  idealLineLead,
  ...props
}: ProgressChartCardProps) => {
  const caption = buildProgressChartCaption(chart);
  const idealLineExplainer =
    [
      idealLineLead !== undefined && chart.idealLine !== null ? idealLineLead : "",
      firstPointNote ?? "",
    ]
      .map((sentence) => {
        return sentence.trim();
      })
      .filter(Boolean)
      .join(" ") || null;
  const showMessageAction = chart.status?.code === PROGRESS_STATUS_CODE.WATCHING;

  return (
    <Card className={cardLayout({ className })} {...props}>
      <header className="flex flex-col-reverse items-start gap-2 sm:flex-row sm:justify-between sm:gap-3">
        <div className="flex flex-col gap-1">
          <Heading level={3}>{chart.title}</Heading>

          <p className="text-sm text-content-muted">{chart.subCaption}</p>
        </div>

        {chart.status === null ? null : (
          <span className={statusPill({ tone: chart.status.tone })}>{chart.status.label}</span>
        )}
      </header>

      <ProgressChartPlot chart={chart} />

      {caption === null ? null : <p className="text-content-secondary">{caption}</p>}

      {idealLineExplainer === null ? null : (
        <p className="text-sm text-content-muted">{idealLineExplainer}</p>
      )}

      {showMessageAction ? (
        <Button className="self-start" size="sm" variant="secondary-dark" asChild>
          <Link
            onClick={() => {
              return stashCareTeamMessageTopic(chart.title);
            }}
            to="/messages"
          >
            {MESSAGE_CARE_TEAM_LABEL}
          </Link>
        </Button>
      ) : null}
    </Card>
  );
};
